/**
 * change-password-modal.component.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, OnInit } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, Validators }
  from "@angular/forms";
import { DialogRef, ModalComponent } from "angular2-modal";
import { BSModalContext } from "angular2-modal/plugins/bootstrap";
import { Observable } from "rxjs/Rx";

import { ConfigurationService } from "../configuration/configuration.service";
import { Message } from "../server/messages/general/";
import { oneline } from "../support/utilities/functions";
import { childErrors, CustomValidators } from "../support/validators";
import { AuthService } from "./auth.service";

export interface ChangePasswordModalContext {
  enforced : boolean;
}

@Component({
  template: `
  <div class="modal-header">
    <button
      type="button"
      class="close"
      (click)="dialog.dismiss()"
      aria-label="Close">
      <span aria-hidden="true">&times;</span>
    </button>
    <h4 class="modal-title">
      Change Password
    </h4>
  </div>
  <div class="modal-body">
    <p *ngIf="enforced" class="text-info">
      The application requires you to change your password before continuing.
    </p>
    <form [formGroup]="form" (submit)="submit()">
      <div class="form-group">
        <label>Current Password</label>
        <input type="password" class="form-control" [formControl]="oldPassword">
      </div>
      <div class="form-group">
        <label>New Password</label>
        <input type="password" class="form-control" [formControl]="newPassword">
      </div>
      <div class="form-group">
        <password-strength-analysis
          [results]="strengthAnalysis"
          [minMagnitude]="config.protocol.minimumPasswordMagnitude">
        </password-strength-analysis>
      </div>
      <div class="form-group">
        <label>Repeat New Password</label>
        <input
          type="password"
          class="form-control"
          [formControl]="newPasswordDuplicate">
      </div>
    </form>
  </div>

  <div class="modal-footer">
    <button
      *ngIf="!enforced"
      type="button"
      class="btn btn-default"
      (click)="dialog.dismiss()">
      Cancel
    </button>
    <button
      type="button"
      class="btn btn-primary"
      (click)="submit()">
      Submit
    </button>
  </div>
  `,
  styles: [
    "/deep/ prettyjson pre {outline: none !important;}",
    "/deep/ #sendButton {position: relative; top: -8px;}"
  ]
})
export class ChangePasswordModalComponent
  implements ModalComponent<ChangePasswordModalContext>, OnInit
{
  public oldPassword : FormControl;
  public newPassword : FormControl;
  public newPasswordDuplicate : FormControl;
  public form : FormGroup;

  public strengthAnalysis : ZxcvbnResult;

  public enforced : boolean;

  constructor (
    public dialog : DialogRef<ChangePasswordModalContext>,
    public auth : AuthService,
    public config : ConfigurationService
  )
  {
      this.enforced = dialog.context.enforced || false;
  }

  public ngOnInit () : void
  {
    this.oldPassword = new FormControl("", Validators.required);
    this.newPassword = new FormControl("", Validators.required);
    this.newPasswordDuplicate = new FormControl(
      "",
      [Validators.required, CustomValidators.equalsTo(this.newPassword)]
    );
    this.form = new FormGroup({
      oldPassword: this.oldPassword,
      newPassword: this.newPassword,
      newPasswordDuplicate: this.newPasswordDuplicate
    });

    this.newPassword.valueChanges.debounceTime(200).subscribe((pw : string) => {
      this.strengthAnalysis = Zxcvbn(pw);
    });
  }

  public submit () : void
  {
    if (!this.form.valid)
    {
      if (this.newPasswordDuplicate.errors.equalsTo)
      {
        alert("Your passwords do not match!");
        return;
      }
      // TODO: detect specific problems!
      console.warn("Encountered errors:", childErrors(this.form));
      return alert("There are errors with the form!");
    }

    if (this.strengthAnalysis.guesses_log10
        < this.config.protocol.minimumPasswordMagnitude)
    {
      alert(
        oneline`Your password is not strong enough.
                (Consider using a sequence of four or more random words.)`);
      return;
    }

    this.auth.changePassword(this.newPassword.value, this.oldPassword.value)
      .subscribe((result : Message) => {
        if ((result as any).ok !== false)
        {
          this.dialog.close(result);
        }
      });
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/auth/change-password-modal.component.ts